import {
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getInvoiceById } from "api/invoice.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_DOWNLOAD_TITLE,
} from "common/constants";
import {
  escapeNewLine,
  formatDate,
  formatNZD,
  printPage,
  sanitizeText,
} from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import { PRINT_MARGIN } from "components/layouts/consts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useState } from "react";
import { InvoiceExportSummary } from "types/model.types";

interface IAdminInvoicePreviewProps {
  id: number;
}
const AdminInvoicePreview: React.FC<IAdminInvoicePreviewProps> = ({ id }) => {
  const [exporting, setExporting] = useState(false);
  const [initialData, setInitialData] = useState<InvoiceExportSummary | null>(
    null,
  );

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    const fetchData = async () => {
      try {
        const response = await safeApiCall(getInvoiceById(id));
        if (mounted) setInitialData(response ?? {});
      } catch (error) {
        logError(error);
        notifications.show({
          message: "Could not fetch invoice details.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  const exportToPDF = async () => {
    setExporting(true);
    const elementsToHide = document.querySelectorAll(".no-print");
    try {
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
      const element = document.getElementById("preview-content");
      if (!element) return;

      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const usableWidth = width - PRINT_MARGIN * 2;
      const usableHeight = (canvas.height * usableWidth) / canvas.width;

      pdf.addImage(
        imgData,
        "PNG",
        PRINT_MARGIN,
        PRINT_MARGIN,
        usableWidth,
        usableHeight,
      );
      pdf.save(`Order_${id}.pdf`);

      notifications.show({
        title: MSG_DEFAULT_DOWNLOAD_TITLE,
        message: "Your order has been saved as a PDF.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Warn,
      });
    } catch (error) {
      notifications.show({
        title: MSG_DEFAULT_DOWNLOAD_TITLE,
        message: "Something went wrong during PDF export.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      console.error(error);
    } finally {
      setExporting(false);
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
    }
  };
  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "none";
    });

    const preview = document.getElementById("preview-content");
    if (!preview) {
      return;
    }
    printPage(preview.innerHTML);

    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "";
    });
  };

  return (
    <>
      {!exporting && (
        <Group justify="flex-end" mb="md" className="no-print">
          <Button onClick={handlePrint} variant="default">
            Print
          </Button>
          <Button
            onClick={exportToPDF}
            variant="outline"
            color="blue"
            disabled={exporting}
          >
            {exporting ? <Loader size="xs" /> : "Export PDF"}
          </Button>
        </Group>
      )}
      <Card withBorder shadow="sm" padding="lg" radius="md">
        <div id="preview-content" className="print-container">
          <PreviewHeader />

          <Center w={"100%"} mb="md" className="print-padBottom">
            <Text size="lg" style={{ fontSize: "1.5rem" }} fw={700}>
              *** Invoice ***
            </Text>
          </Center>

          <Table mb="md" className="print-padBottom">
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Group justify="space-between" mb="md" grow wrap="nowrap">
                    <Text size="md" fw={700}>
                      Date Ordered:
                    </Text>
                    <Text size="md" fw={700} c="dimmed">
                      {formatDate(
                        initialData?.summary.createdOn ??
                          new Date().toDateString(),
                      )}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap={3}>
                    <Group justify="flex-end">
                      <Text size="md" fw={700}>
                        TAX INVOICE NO:
                      </Text>
                      <Text
                        size="md"
                        className="alignr"
                        w={120}
                        fw={700}
                        c="dimmed"
                      >
                        {initialData?.id}
                      </Text>
                    </Group>
                    <Group justify="flex-end">
                      <Text size="md" className="alignr" fw={700}>
                        CUSTOMER NO:
                      </Text>

                      <Text
                        size="md"
                        fw={700}
                        w={120}
                        className="alignr"
                        c="dimmed"
                      >
                        {initialData?.invoiceTo.id}
                      </Text>
                    </Group>
                    <Group justify="flex-end">
                      <Text size="md" fw={700}>
                        GST:
                      </Text>
                      <Text
                        size="md"
                        fw={700}
                        w={120}
                        className="alignr"
                        c="dimmed"
                      >
                        {process.env.REACT_APP_GST_NO ?? "20-898-763"}
                      </Text>
                    </Group>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Table mb="md" className="print-padBottom">
            <Table.Tbody>
              <Table.Tr style={{ display: "table-row" }} className="no-border">
                <Table.Td style={{ width: "50%" }}>
                  <Text size="md" fw={700}>
                    Invoice To
                  </Text>
                  <hr />
                </Table.Td>
                <Table.Td style={{ width: "50%" }}>
                  <Text size="md" fw={700}>
                    Deliver To
                  </Text>
                  <hr />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: escapeNewLine(
                        [
                          initialData?.invoiceTo.debtorName,
                          initialData?.invoiceTo.addressLine1,
                          initialData?.invoiceTo.addressLine2,
                          initialData?.invoiceTo.addressLine3,
                        ]
                          .filter(Boolean)
                          .join("\n"),
                      ),
                    }}
                  />
                </Table.Td>
                <Table.Td>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        String(initialData?.invoiceTo).trim() ===
                        String(initialData?.deliverTo).trim()
                          ? (initialData?.deliverTo.debtorName ?? "")
                          : [
                              initialData?.deliverTo.debtorName,
                              initialData?.deliverTo.addressLine1,
                              initialData?.deliverTo.addressLine2,
                              initialData?.deliverTo.addressLine3,
                            ]
                              .filter(Boolean)
                              .join("\n"),
                    }}
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Table mb="md" className="print-padBottom">
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Text size="md" fw={700}>
                    Your Order No: &nbsp;&nbsp;&nbsp;{" "}
                    {initialData?.summary.yourOrderNo ??
                      initialData?.summary.customerOrderNo}
                  </Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          {/* Render logic for orderDetails array here */}
          <Table striped className="print-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th className="description">Description</Table.Th>
                <Table.Th className="money">Ord</Table.Th>
                <Table.Th className="money">Sup</Table.Th>
                <Table.Th className="money">E/Price</Table.Th>
                <Table.Th className="money">C/Price</Table.Th>
                <Table.Th className="money">Dis %</Table.Th>
                <Table.Th className="money">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <Table.Tr>
                <Table.Td>{""}</Table.Td>
                <Table.Td>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeText(initialData?.summary.notes ?? ""),
                    }}
                  />
                </Table.Td>
                <Table.Td colSpan={6}> </Table.Td>
              </Table.Tr>
              {initialData?.orderDetails.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{`${
                    item.id
                  }${item.productCode.toUpperCase()}`}</Table.Td>
                  <Table.Td className="description">
                    {item.description}
                  </Table.Td>
                  <Table.Td className="money">{item.ordered ?? 0}</Table.Td>
                  <Table.Td className="money">{item.supplied ?? 0}</Table.Td>
                  <Table.Td className="money">
                    {formatNZD(item.eachPrice)}
                  </Table.Td>
                  <Table.Td className="money">
                    {formatNZD(item.cartonPrice)}
                  </Table.Td>
                  <Table.Td className="money">
                    {item.discount > 0 ? item.discount : ""}
                  </Table.Td>
                  <Table.Td className="money">{formatNZD(item.total)}</Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td>{""}</Table.Td>
                <Table.Td>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeText(initialData?.summary.notes2 ?? ""),
                    }}
                  />
                </Table.Td>
                <Table.Td colSpan={6}>{""}</Table.Td>
              </Table.Tr>

              <Table.Tr>
                <Table.Td colSpan={6} />
                <Table.Td className="money">Sub Total</Table.Td>
                <Table.Td className="money">
                  {formatNZD(initialData?.summary.total ?? 0)}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td colSpan={6} />
                <Table.Td className="money">GST</Table.Td>
                <Table.Td className="money">
                  {formatNZD(initialData?.summary.gst ?? 0)}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td colSpan={6} />
                <Table.Td className="money money-focal">TOTAL</Table.Td>
                <Table.Td className="money money-focal">
                  {formatNZD(
                    (initialData?.summary.total ?? 0) +
                      (initialData?.summary.gst ?? 0),
                  )}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      </Card>
    </>
  );
};

export default AdminInvoicePreview;
